import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  RowSelectionModule,
  RowApiModule,
} from "ag-grid-community";
import {
  SetFilterModule,
  PaginationModule,
} from "ag-grid-enterprise";
import {
  PamGrid,
  CurrencyRenderer,
  isPermissionDenied,
  getPermissionDeniedMessage,
} from '@1823-partners/core';
import { fetchPositions } from '../api';

// Persistent state outside component to maintain across modal sessions
let persistentFilters = {
  bv: { min: '', max: '' },
  at1: '',
  at2: '',
  at3: '',
  sec_owner: ''
};

let persistentGridFilters = null;
let persistentSortModel = null;
let persistentSelectedCusips = [];

const PortfolioBuilderModal = ({ 
  open, 
  onClose, 
  onSelectCusips, 
  env, 
  asofdt,
  selectedCusips = []
}) => {
  const [positions, setPositions] = useState([]);
  const [filteredPositions, setFilteredPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [filters, setFilters] = useState(persistentFilters);
  const [gridApi, setGridApi] = useState(null);
  const gridRef = useRef(null);
  // Only the newest loadPositions may write shared state. The effect below
  // re-fires when the modal reopens or env/asofdt changes, so loads overlap:
  // without this, an older denial can land after newer positions have rendered
  // and offer a Request access button for a different environment or date.
  const requestIdRef = useRef(0);

  // Load positions data when modal opens
  useEffect(() => {
    if (open && env && asofdt) {
      loadPositions();
    }
  }, [open, env, asofdt]);

  // Apply filters when filter values change
  useEffect(() => {
    applyFilters();
  }, [filters, positions]);

  // Restore selected rows when positions load and we have persistent selections
  useEffect(() => {
    if (gridApi && positions.length > 0 && persistentSelectedCusips.length > 0) {
      restoreSelectedRows();
    }
  }, [gridApi, positions]);

  const loadPositions = async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchPositions(env, asofdt);
      // A newer load started while this one was in flight — for a different
      // env/asofdt, or a reopen — so these rows are not what is on screen.
      if (requestId !== requestIdRef.current) return;
      setPositions(response.data);
      setFilteredPositions(response.data);
    } catch (err) {
      console.error('Error loading positions:', err);
      // Same rule for failures: a superseded denial must not replace the state
      // of the load that took over.
      if (requestId !== requestIdRef.current) return;
      if (isPermissionDenied(err)) {
        // /positions carries no @require_permission, so there is no role to
        // request here; the Message variant keeps the Permissions-app URL.
        setError(getPermissionDeniedMessage(err));
      } else {
        setError('Failed to load positions data. Please try again.');
      }
    } finally {
      // Only the newest load owns the spinner; clearing it from a superseded one
      // would hide that a newer load is still running.
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const applyFilters = () => {
    let filtered = [...positions];

    // Apply BV filter
    if (filters.bv.min !== '' || filters.bv.max !== '') {
      filtered = filtered.filter(row => {
        const value = parseFloat(row.BV) || 0;
        const min = filters.bv.min === '' ? -Infinity : parseFloat(filters.bv.min);
        const max = filters.bv.max === '' ? Infinity : parseFloat(filters.bv.max);
        return value >= min && value <= max;
      });
    }

    // Apply AT1 filter
    if (filters.at1 !== '') {
      filtered = filtered.filter(row => {
        const value = (row.AT1 || '').toString().toLowerCase();
        const filterValue = filters.at1.toLowerCase();
        return value.includes(filterValue);
      });
    }

    // Apply sec_owner filter
    if (filters.sec_owner !== '') {
      filtered = filtered.filter(row => {
        const value = (row.sec_owner || '').toString().toLowerCase();
        const filterValue = filters.sec_owner.toLowerCase();
        return value.includes(filterValue);
      });
    }

    // Apply AT2 filter
    if (filters.at2 !== '') {
      filtered = filtered.filter(row => {
        const value = (row.AT2 || '').toString().toLowerCase();
        const filterValue = filters.at2.toLowerCase();
        return value.includes(filterValue);
      });
    }

    // Apply AT3 filter
    if (filters.at3 !== '') {
      filtered = filtered.filter(row => {
        const value = (row.AT3 || '').toString().toLowerCase();
        const filterValue = filters.at3.toLowerCase();
        return value.includes(filterValue);
      });
    }

    setFilteredPositions(filtered);
  };

  const handleFilterChange = (metric, type, value) => {
    let newFilters;
    if (metric === 'bv') {
      newFilters = {
        ...filters,
        [metric]: {
          ...filters[metric],
          [type]: value
        }
      };
    } else {
      // For AT1, AT2, AT3 - simple string values
      newFilters = {
        ...filters,
        [metric]: value
      };
    }
    setFilters(newFilters);
    persistentFilters = newFilters; // Update persistent state
  };

  const clearFilters = () => {
    const clearedFilters = {
      bv: { min: '', max: '' },
      at1: '',
      at2: '',
      at3: '',
      sec_owner: ''
    };
    setFilters(clearedFilters);
    persistentFilters = clearedFilters; // Update persistent state
    
    // Clear grid filters and sorting
    if (gridApi) {
      gridApi.setFilterModel(null);
      if (typeof gridApi.setSortModel === 'function') {
        try {
          gridApi.setSortModel(null);
        } catch (error) {
          console.warn('Error clearing sort model:', error);
        }
      }
      persistentGridFilters = null;
      persistentSortModel = null;
    }
  };

  const onGridReady = (params) => {
    setGridApi(params.api);

    // Restore saved grid filters if they exist
    if (persistentGridFilters) {
      params.api.setFilterModel(persistentGridFilters);
    }

    // Restore saved sort model if it exists
    if (persistentSortModel && typeof params.api.setSortModel === 'function') {
      try {
        params.api.setSortModel(persistentSortModel);
      } catch (error) {
        console.warn('Error setting sort model:', error);
      }
    }
  };

  const handleFirstDataRendered = (params) => {
    // Once the grid has actually put the rows on screen,
    // select the ones matching `selectedCusips`:
    params.api.deselectAll();
    selectedCusips.forEach(cusip =>
      params.api.forEachNode(node => {
        if (node.data?.CUSIP === cusip) {
          node.setSelected(true);
        }
      })
    );
    // Update your local state if needed:
    const selected = params.api.getSelectedNodes().map(n => n.data);
    setSelectedRows(selected);
  };

  const onSelectionChanged = () => {
    if (gridApi) {
      const selectedNodes = gridApi.getSelectedNodes();
      const selectedData = selectedNodes.map(node => node.data);
      setSelectedRows(selectedData);
      
      // Update persistent selected CUSIPs
      persistentSelectedCusips = selectedData.map(row => row.CUSIP).filter(cusip => cusip);
    }
  };

  const onFilterChanged = () => {
    if (gridApi) {
      persistentGridFilters = gridApi.getFilterModel();
    }
  };

  const onSortChanged = () => {
    if (gridApi && typeof gridApi.getSortModel === 'function') {
      try {
        persistentSortModel = gridApi.getSortModel();
      } catch (error) {
        console.warn('Error getting sort model:', error);
      }
    }
  };

  const restoreSelectedRows = () => {
    if (gridApi && persistentSelectedCusips.length > 0) {
      // Select rows that match the persistent CUSIPs
      gridApi.forEachNode(node => {
        if (node.data && persistentSelectedCusips.includes(node.data.CUSIP)) {
          node.setSelected(true);
        }
      });
    }
  };

  const handleSelectCusips = () => {
    const cusips = selectedRows.map(row => row.CUSIP).filter(cusip => cusip);
    onSelectCusips(cusips);
    onClose();
  };

  // Column definitions for AG Grid.
  // Note: the row-select checkbox column is rendered by PamGrid via the
  // `selectable` prop (v32.2+ rowSelection.checkboxes), not declared here.
  const columnDefs = useMemo(() => [
    {
      headerName: 'CUSIP',
      field: 'CUSIP',
      width: 150,
      sortable: true,
      filter: true
    },
    {
      headerName: 'Security Owner',
      field: 'SEC_OWNER',
      width: 160,
      sortable: true,
      filter: true
    },
    {
      headerName: 'BV',
      field: 'BV',
      width: 150,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellRenderer: CurrencyRenderer,
    },
    {
      headerName: 'AT1',
      field: 'AT1',
      width: 150,
      sortable: true,
      filter: true
    },
    {
      headerName: 'AT2',
      field: 'AT2',
      width: 150,
      sortable: true,
      filter: true
    },
    {
      headerName: 'AT3',
      field: 'AT3',
      width: 150,
      sortable: true,
      filter: true
    }
  ], []);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    sortable: true,
    filter: true,
    floatingFilter: true
  }), []);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { height: '85vh', maxWidth: '1100px' }
      }}
    >
      <DialogTitle>
        <Typography variant="h6">Build Portfolio</Typography>
        <Typography variant="body2" color="text.secondary">
          Filter positions by metrics and select CUSIPs for your scenario analysis
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {/* Warning for missing required fields */}
        {(!env || !asofdt) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Please fill out both Scenario Env and ASOFDT in the form before using Portfolio Builder.
          </Alert>
        )}

        {/* Selection Summary and Controls */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            {selectedRows.length > 0 && (
              <Typography variant="body2" color="primary">
                {selectedRows.length} position{selectedRows.length !== 1 ? 's' : ''} selected
              </Typography>
            )}
          </Box>
          <Button 
            onClick={clearFilters}
            variant="outlined"
            size="small"
            sx={{ ml: 2 }}
          >
            Clear Filters
          </Button>
        </Box>

        {/* Loading State */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Data Grid */}
        <PamGrid
          ref={gridRef}
          modules={[RowSelectionModule, RowApiModule, SetFilterModule, PaginationModule]}
          columnDefs={columnDefs}
          rowData={filteredPositions}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
          onFilterChanged={onFilterChanged}
          onSortChanged={onSortChanged}
          onFirstDataRendered={handleFirstDataRendered}
          selectable={true}
          pagination={true}
          paginationPageSize={50}
          height={500}
          showQuickFilter={false}
          showFooter={false}
          containerSx={{ minWidth: '1050px' }}
        />

        {/* Summary */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredPositions.length} of {positions.length} positions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedRows.length} selected
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSelectCusips}
          variant="contained"
          disabled={selectedRows.length === 0}
          sx={{ backgroundColor: 'lightgrey', color: 'black' }}
        >
          Select CUSIPs ({selectedRows.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PortfolioBuilderModal;